import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PublicClientApplication } from "@azure/msal-browser";
import { useAuth } from "../context/AuthContext";
import { UserRole } from "../types/auth.types";
import { authService } from "../services/authService";
import { setOwnerAccessToken } from "../core/auth/ownerAccessToken";

type LoginMethod = "selection" | "email";
type MsalPublicClientApplication = PublicClientApplication;

const azureClientId = "cc3f5d48-290e-46e8-b9f0-97185de6161c";
const MARKETPLACE_SUBSCRIPTION_URL =
  "https://marketplace.microsoft.com/en-us/checkout/ninebitcomputing.caseflow?tab=Overview";

type SubscriberLoginErrorCode =
  | "ERR_JWT_EXPIRED"
  | "SUBSCRIPTION_NOT_FOUND"
  | "SUBSCRIPTION_NOT_SUBSCRIBED";

const SUBSCRIBER_LOGIN_ERROR_MESSAGES: Record<SubscriberLoginErrorCode, string> = {
  ERR_JWT_EXPIRED: "Your Azure login session expired. Please sign in again.",
  SUBSCRIPTION_NOT_FOUND: "No active marketplace subscription was found for this account.",
  SUBSCRIPTION_NOT_SUBSCRIBED:
    "This marketplace subscription is not active yet. Complete subscription activation and try again.",
};

const extractError = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message?: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }

  return fallback;
};

const getErrorStatusCode = (error: unknown): number | null => {
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    typeof (error as { response?: { status?: unknown } }).response?.status === "number"
  ) {
    return (error as { response: { status: number } }).response.status;
  }

  return null;
};

const getSubscriberLoginErrorCode = (error: unknown): SubscriberLoginErrorCode | null => {
  if (
    typeof error !== "object" ||
    error === null ||
    !("response" in error) ||
    typeof (error as { response?: { data?: unknown } }).response?.data !== "object" ||
    (error as { response?: { data?: unknown } }).response?.data === null
  ) {
    return null;
  }

  const data = (error as { response: { data: Record<string, unknown> } }).response.data;
  const directErrorCode = typeof data.error === "string" ? data.error : null;
  const nestedError =
    typeof data.error === "object" && data.error !== null
      ? (data.error as Record<string, unknown>)
      : null;
  const possibleCodeValues: unknown[] = [
    directErrorCode,
    data.code,
    data.error_code,
    data.errorCode,
    nestedError?.code,
    nestedError?.error_code,
    nestedError?.errorCode,
  ];

  for (const value of possibleCodeValues) {
    if (
      value === "ERR_JWT_EXPIRED" ||
      value === "SUBSCRIPTION_NOT_FOUND" ||
      value === "SUBSCRIPTION_NOT_SUBSCRIBED"
    ) {
      return value;
    }
  }

  return null;
};

const getSubscriberLoginField = (
  payload: Record<string, unknown>,
  keys: readonly string[],
): string | null => {
  for (const key of keys) {
    const value = payload[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return null;
};

const getScopesFromPayload = (payload: Record<string, unknown>): string[] => {
  const rawScopes = payload.scopes;
  if (Array.isArray(rawScopes)) {
    return rawScopes
      .filter((scope): scope is string => typeof scope === "string")
      .map((scope) => scope.trim().toUpperCase())
      .filter(Boolean);
  }

  if (typeof rawScopes === "string") {
    return rawScopes
      .split(/[,\s]+/)
      .map((scope) => scope.trim().toUpperCase())
      .filter(Boolean);
  }

  return [];
};

const decodeJwtPayload = (token: string): Record<string, unknown> => {
  const parts = token.split(".");
  if (parts.length < 2 || !parts[1]) {
    throw new Error("Invalid access token format.");
  }

  const normalized = parts[1].replace(/-/g, "+").replace(/_/g, "/");
  const padding = "=".repeat((4 - (normalized.length % 4)) % 4);
  const binary = atob(`${normalized}${padding}`);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  const decoded = new TextDecoder().decode(bytes);
  const parsed = JSON.parse(decoded);

  if (typeof parsed !== "object" || parsed === null) {
    throw new Error("Invalid access token payload.");
  }
  return parsed as Record<string, unknown>;
};

const extractAccessTokenFromSubscriberLogin = (payload: unknown): string | null => {
  if (typeof payload !== "object" || payload === null) {
    return null;
  }

  return getSubscriberLoginField(payload as Record<string, unknown>, [
    "accessToken",
  ]);
};

const extractAccessTokenFromEmailOtpVerify = (payload: unknown): string | null => {
  if (typeof payload !== "object" || payload === null) {
    return null;
  }

  return getSubscriberLoginField(payload as Record<string, unknown>, [
    "control_plane_jwt",
    "accessToken",
  ]);
};

const extractSubscriberIdFromAccessToken = (accessToken: string): string => {
  const payload = decodeJwtPayload(accessToken);
  const subscriberId = getSubscriberLoginField(payload, [
    "tid"
  ]);

  if (!subscriberId) {
    throw new Error("accessToken payload is missing tid.");
  }

  return subscriberId;
};

const hasAdminScopeInAccessToken = (accessToken: string): boolean => {
  const payload = decodeJwtPayload(accessToken);
  const scopes = getScopesFromPayload(payload);
  return scopes.includes("ADMIN");
};

const resolveEmailOtpRoleFromAccessToken = (accessToken: string): UserRole | null => {
  const payload = decodeJwtPayload(accessToken);
  const scopes = getScopesFromPayload(payload);

  if (scopes.includes("CLIENT")) {
    return "CLIENT";
  }
  if (scopes.includes("ADVISOR") || scopes.includes("ADVISER")) {
    return "ADVISER";
  }
  if (scopes.includes("ASSISTANT")) {
    return "ASSISTANT";
  }
  return null;
};

const buildSessionFromAccessToken = (
  accessToken: string,
  fallbackEmail: string,
  role: UserRole,
  explicitUserId?: string,
): { id: string; name: string; role: UserRole; firmId?: string } => {
  const payload = decodeJwtPayload(accessToken);

  const id =
    (explicitUserId?.trim() || null) ||
    getSubscriberLoginField(payload, [
      "user_id",
      "userId",
      "sub",
      "tid",
      "tenant_id",
      "tenantId",
      "email",
    ]) || fallbackEmail;

  if (!id) {
    throw new Error("Token payload is missing user identity.");
  }

  const name =
    getSubscriberLoginField(payload, [
      "name",
      "username",
      "email",
    ]) || fallbackEmail;

  const firmId = getSubscriberLoginField(payload, [
    "tid",
    "tenant_id",
    "tenantId",
  ]) || undefined;

  return {
    id,
    name,
    role,
    firmId,
  };
};

const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [loginMethod, setLoginMethod] = useState<LoginMethod>("selection");
  const [msalClient, setMsalClient] = useState<MsalPublicClientApplication | null>(null);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [emailOtpStartSessionId, setEmailOtpStartSessionId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [azureSubmitting, setAzureSubmitting] = useState(false);
  const [azureStatus, setAzureStatus] = useState<string | null>(null);
  const [azureError, setAzureError] = useState<string | null>(null);
  const [azureErrorCode, setAzureErrorCode] = useState<SubscriberLoginErrorCode | null>(null);

  const handleEmailOtpStart = async () => {
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) {
      setError("Email is required.");
      return;
    }

    setSubmitting(true);
    setError(null);
    setStatus(null);
    try {
      const result = await authService.startEmailOtp(trimmedEmail);
      const minutes = Math.ceil(result.expires_in / 60);
      setEmailOtpStartSessionId(result.email_otp_start_session_id);
      setOtp("");
      setStatus(`OTP sent to your email. It expires in ${minutes} minute${minutes === 1 ? "" : "s"}.`);
    } catch (err) {
      const detail =
        typeof err === "object" &&
        err !== null &&
        "response" in err &&
        typeof (err as { response?: { data?: { detail?: string } } }).response?.data?.detail ===
          "string"
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : null;
      setError(detail ?? "Unable to start email OTP.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEmailOtpVerify = async () => {
    const trimmedOtp = otp.trim();
    const trimmedEmail = email.trim().toLowerCase();
    if (!emailOtpStartSessionId) {
      setError("Please request OTP again.");
      return;
    }
    if (!trimmedOtp) {
      setError("OTP is required.");
      return;
    }

    setSubmitting(true);
    setError(null);
    setStatus(null);

    try {
      const result = await authService.verifyEmailOtp(emailOtpStartSessionId, trimmedOtp);
      const accessToken = extractAccessTokenFromEmailOtpVerify(result);
      if (!accessToken) {
        throw new Error("OTP verify response is missing control_plane_jwt.");
      }

      const role = resolveEmailOtpRoleFromAccessToken(accessToken);
      if (!role) {
        throw new Error("User do not have enough permission.");
      }

      setOwnerAccessToken(accessToken);

      const session = buildSessionFromAccessToken(
        accessToken,
        trimmedEmail,
        role,
        result.user_id,
      );
      login(session.name, session.role, session.id, session.firmId);

      if (role === "ADVISER") {
        navigate("/adviser");
      } else if (role === "ASSISTANT") {
        navigate("/assistant");
      } else if (role === "CLIENT") {
        navigate("/client");
      } else {
        throw new Error("User do not have enough permission.");
      }
    } catch (err) {
      const detail =
        typeof err === "object" &&
        err !== null &&
        "response" in err &&
        typeof (err as { response?: { data?: { detail?: string } } }).response?.data?.detail ===
          "string"
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : null;
      setError(detail ?? extractError(err, "Unable to verify OTP."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEmailLoginClick = () => {
    setError(null);
    setStatus(null);
    setOtp("");
    setEmailOtpStartSessionId(null);
    setLoginMethod("email");
  };

  const handleBackToSelection = () => {
    setError(null);
    setStatus(null);
    setOtp("");
    setEmailOtpStartSessionId(null);
    setLoginMethod("selection");
  };

  const getOrCreateMsalClient = async (): Promise<MsalPublicClientApplication> => {
    if (!azureClientId) {
      throw new Error(
        "Azure config missing. Set REACT_APP_AZURE_CLIENT_ID.",
      );
    }

    if (msalClient) {
      return msalClient;
    }

    const instance = new PublicClientApplication({
      auth: {
        clientId: azureClientId,
        authority: `https://login.microsoftonline.com/organizations`,
        redirectUri: window.location.origin,
      },
      cache: {
        cacheLocation: "sessionStorage",
      },
    });

    await instance.initialize();
    setMsalClient(instance);

    return instance;
  };

  const handleAzureLogin = async () => {
    setAzureSubmitting(true);
    setAzureError(null);
    setAzureErrorCode(null);
    setAzureStatus("Opening Microsoft sign-in popup...");

    try {
      const client = await getOrCreateMsalClient();
      const response = await client.loginPopup({
        scopes: ["User.Read"],
      });

      console.log("Azure login popup response:", response);

      if (!response.idToken) {
        throw new Error("Azure login succeeded but no idToken was returned.");
      }

      const account =
        response.account || client.getActiveAccount() || client.getAllAccounts()[0] || null;
      if (account) {
        client.setActiveAccount(account);
      }

      setAzureStatus("Azure login successful. Validating token with Azure Function...");

      const subscriberLoginResult = await authService.loginWithAzure(response.idToken);
      const accessToken = extractAccessTokenFromSubscriberLogin(subscriberLoginResult);
      if (!accessToken) {
        throw new Error("Subscriber login response is missing accessToken.");
      }
      const subscriberId = extractSubscriberIdFromAccessToken(accessToken);

      setOwnerAccessToken(accessToken);

      const fallbackName = account?.name?.trim() || account?.username?.trim() || "Owner";
      login(fallbackName, "OWNER", subscriberId, subscriberId);

      if (hasAdminScopeInAccessToken(accessToken)) {
        navigate("/admin");
      } 
      return;
    } catch (azureLoginError) {
      const subscriberLoginErrorCode = getSubscriberLoginErrorCode(azureLoginError);
      if (subscriberLoginErrorCode) {
        setAzureErrorCode(subscriberLoginErrorCode);
        setAzureError(SUBSCRIBER_LOGIN_ERROR_MESSAGES[subscriberLoginErrorCode]);
        setAzureStatus(null);
        return;
      }

      const statusCode = getErrorStatusCode(azureLoginError);

      if (statusCode === 404) {
        setAzureErrorCode(null);
        setAzureError(null);
        setAzureStatus("Azure login successful, but Azure Function endpoint returned 404.");
        return;
      }

      setAzureErrorCode(null);
      setAzureError(extractError(azureLoginError, "Azure login failed."));
      setAzureStatus(null);
    } finally {
      setAzureSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-shell">
        <aside className="login-brand card">
          <div className="logo" role="img" aria-label="CaseFlow" />
          <span className="badge badge-blue login-badge">
            <i className="ti ti-check" /> Visitor visa MVP
          </span>
          <div className="login-brand-copy">
            <h1 className="login-title">CaseFlow Workspaces</h1>
            <p className="login-subtitle">
              Access the same case lifecycle from the right role-specific
              dashboard for your team.
            </p>
          </div>
          <div className="login-workspaces">
            <span className="login-workspace-chip">
              <i className="ti ti-briefcase" /> Immigration adviser
            </span>
            <span className="login-workspace-chip">
              <i className="ti ti-clipboard-list" /> Assistant workspace
            </span>
            <span className="login-workspace-chip">
              <i className="ti ti-user" /> Client portal
            </span>
            {/* <span className="login-workspace-chip">
              <i className="ti ti-chart-bar" /> Firm owner
            </span> */}
          </div>

          <div className="login-points">
            <div className="login-point">
              <i className="ti ti-layout-grid" />
              <div>
                <div className="login-point-title">Role-specific dashboards</div>
                <div className="login-point-sub">
                  Each user lands directly in the view designed for their role.
                </div>
              </div>
            </div>
            <div className="login-point">
              <i className="ti ti-route-alt-left" />
              <div>
                <div className="login-point-title">Shared case timeline</div>
                <div className="login-point-sub">
                  Tasks, documents, and milestones stay aligned across teams.
                </div>
              </div>
            </div>
            <div className="login-point">
              <i className="ti ti-file-check" />
              <div>
                <div className="login-point-title">Execution-ready workflow</div>
                <div className="login-point-sub">
                  Track case progress from lead intake through submission.
                </div>
              </div>
            </div>
          </div>
        </aside>

        <section className="login-form-card card">
          <div className="card-hd">
            <i className="ti ti-lock" /> Sign in to CaseFlow
          </div>
          {loginMethod === "selection" ? (
            <>
              <p className="login-form-copy">
                Choose a sign-in method to continue to your workspace.
              </p>
              <div className="login-method-actions">
                <button
                  className="btn btn-primary login-submit"
                  type="button"
                  onClick={() => {
                    void handleAzureLogin();
                  }}
                  disabled={azureSubmitting}
                >
                  <i className="ti ti-brand-microsoft" />
                  {azureSubmitting ? "Signing in with Azure..." : "Login with Azure"}
                </button>
                <button
                  className="btn login-submit"
                  type="button"
                  onClick={handleEmailLoginClick}
                  disabled={azureSubmitting}
                >
                  <i className="ti ti-id-badge-2" />
                  Login with Email
                </button>
                {azureStatus ? <p className="login-form-help">{azureStatus}</p> : null}
                {azureError ? (
                  <div className="login-error" role="alert">
                    <i className="ti ti-alert-circle" />
                    {azureError}
                    {azureErrorCode === "SUBSCRIPTION_NOT_FOUND" ? (
                      <>
                        {" "}
                        Please buy a new subscription{" "}
                        <a
                          className="login-inline-link"
                          href={MARKETPLACE_SUBSCRIPTION_URL}
                          target="_blank"
                          rel="noreferrer"
                        >
                          here
                        </a>
                        .
                      </>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </>
          ) : (
            <>
              <button
                className="btn login-back"
                type="button"
                onClick={handleBackToSelection}
                disabled={submitting}
              >
                <i className="ti ti-arrow-left" />
                Back
              </button>
              <p className="login-form-copy">
                Enter your registered email to receive your login OTP.
              </p>

              <div className="field" style={{ marginBottom: 14 }}>
                <label htmlFor="login-email">Email</label>
                <input
                  id="login-email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError(null);
                    if (status) setStatus(null);
                    if (emailOtpStartSessionId) setEmailOtpStartSessionId(null);
                    if (otp) setOtp("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      if (emailOtpStartSessionId) {
                        void handleEmailOtpVerify();
                      } else {
                        void handleEmailOtpStart();
                      }
                    }
                  }}
                />
                {emailOtpStartSessionId ? (
                  <div className="field" style={{ marginTop: 10 }}>
                    <label htmlFor="login-otp">OTP</label>
                    <input
                      id="login-otp"
                      placeholder="Enter OTP"
                      value={otp}
                      onChange={(e) => {
                        setOtp(e.target.value);
                        if (error) setError(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          void handleEmailOtpVerify();
                        }
                      }}
                    />
                  </div>
                ) : null}
                {status ? (
                  <p className="login-form-help" role="status">
                    {status}
                  </p>
                ) : null}
                {error ? (
                  <div className="login-error" role="alert">
                    <i className="ti ti-alert-circle" />
                    {error}
                  </div>
                ) : null}
              </div>

              <button
                className="btn btn-primary login-submit"
                onClick={() => {
                  if (emailOtpStartSessionId) {
                    void handleEmailOtpVerify();
                  } else {
                    void handleEmailOtpStart();
                  }
                }}
                disabled={submitting}
              >
                <i className="ti ti-login-2" />
                {submitting
                  ? emailOtpStartSessionId
                    ? "Logging in..."
                    : "Sending OTP..."
                  : emailOtpStartSessionId
                    ? "Login"
                    : "Get OTP"}
              </button>
            </>
          )}
          <p className="login-form-help">
            If you cannot sign in, contact your CaseFlow administrator.
            {" "}
            {/* Need a new firm workspace?{" "}
            <button
              type="button"
              className="login-inline-link"
              onClick={() => navigate("/signup")}
            >
              Create one.
            </button> */}
          </p>
        </section>
      </div>
    </div>
  );
};

export default LoginPage;
